import {
  ApiError,
  formatMoney,
  formatUsPhone,
  queryKeys,
  type RentalReceiveScanResult,
  rentalFeePeriods,
  rentalIntervalUnit,
  UNIT_IDENTIFIER_LABELS,
  unitIdentifierKind,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  RENTAL_STATUS_TONE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { AlertTriangle, CalendarClock, Plus, ScanLine, Search, Wrench } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { DataTable } from '../components/data-table.js'
import {
  firstOfMonth,
  gridDays,
  MonthCalendar,
  type MonthCalendarEntry,
  MonthNav,
  toISODate,
} from '../components/month-calendar.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface RentalRow {
  id: string
  rentalNumber: string
  status: string
  customerId: string
  customerName: string | null
  customerPhone: string | null
  imei: string
  productName: string
  productType: string
  itemCount: number
  startDate: string
  dueDate: string | null
  returnedAt: string | null
  feeCents: number
  billingInterval: string
  depositCents: number
  depositReturnedAt: string | null
  needsAttentionAt: string | null
  createdAt: string
}

const BILLING_LABEL: Record<string, string> = {
  one_time: 'one time',
  daily: '/ day',
  weekly: '/ week',
  monthly: '/ month',
}

function isPastDue(dueDate: string | null, status: string): boolean {
  if (!dueDate || ['returned', 'cancelled', 'not_returned'].includes(status)) return false
  return dueDate < new Date().toISOString().slice(0, 10)
}

/** Just the fields the calendar draws from a repair ticket. */
interface RepairCalendarItem {
  id: string
  ticketNumber: string
  customerName: string | null
  dropOffDate: string | null
  promisedDate: string | null
  workDate: string | null
}

type RentalsView = 'list' | 'calendar'
type CalendarScope = 'rentals' | 'repairs' | 'both'

const PILL = 'block truncate rounded px-1.5 py-0.5 text-2xs'

const DEFAULT_STATUS = 'except_cancelled'

/**
 * Rentals: a phone or SIM handed to a customer under IMEI/ICCID tracking,
 * expected back rather than sold. Same shape as the orders list — infinite
 * scroll, not page-number pagination.
 */
export function RentalsPage() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(DEFAULT_STATUS)
  const [creating, setCreating] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [needsAttention, setNeedsAttention] = useState(false)
  const [prefillUnit, setPrefillUnit] = useState<UnitRow | null>(null)
  const [view, setView] = useState<RentalsView>('list')
  const [scope, setScope] = useState<CalendarScope>('rentals')
  const [monthStart, setMonthStart] = useState(() => firstOfMonth(new Date()))
  // Repairs on the calendar need their own permission; without it the scope
  // switch is hidden and the calendar stays rentals-only.
  const canSeeRepairs = can('repairs:read')
  const effectiveScope: CalendarScope = canSeeRepairs ? scope : 'rentals'

  // Untyped: this route only ever receives these from a "Rent this unit"
  // action on a Provisioning row.
  const deepLink = useSearch({ strict: false }) as {
    newRental?: boolean
    unitId?: string
    unitImei?: string
    productId?: string
    productName?: string
    productType?: string
    locationName?: string
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: act on the link once, on mount
  useEffect(() => {
    if (!deepLink.newRental || !deepLink.unitId) return
    setPrefillUnit({
      id: deepLink.unitId,
      productId: deepLink.productId ?? '',
      productName: deepLink.productName ?? '',
      productType: deepLink.productType ?? '',
      imei: deepLink.unitImei ?? '',
      locationName: deepLink.locationName ?? null,
    })
    setCreating(true)
    void navigate({ to: '/rentals', search: {}, replace: true })
  }, [deepLink.newRental, deepLink.unitId])

  const filters = {
    q: search || undefined,
    status: status === 'all' ? undefined : status,
    needsAttention: needsAttention || undefined,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.rentals.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: RentalRow[]; total: number; page: number; pageSize: number }>('/rentals', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled: view === 'list',
  })
  const rows = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  // Calendar mode: everything whose start or due date falls in the visible
  // 6-week grid. The status filter still applies. `overdue` is a stored status
  // the nightly sweep sets, so filtering on it lags a day; the red pills below
  // are derived from the due date and don't.
  const days = useMemo(() => gridDays(monthStart), [monthStart])
  const from = days[0] ? toISODate(days[0]) : undefined
  const to = days[41] ? toISODate(days[41]) : undefined
  const calendarFilters = {
    from,
    to,
    dateField: 'either',
    status: status === 'all' ? undefined : status,
    needsAttention: needsAttention || undefined,
    pageSize: 500,
  }
  const showRentals = effectiveScope !== 'repairs'
  const showRepairs = effectiveScope !== 'rentals'

  const rentalsCalendar = useQuery({
    queryKey: queryKeys.rentals.list(calendarFilters),
    queryFn: () =>
      api.get<{ items: RentalRow[]; total: number }>('/rentals', { query: calendarFilters }),
    enabled: view === 'calendar' && showRentals,
  })
  // Same key and request as the Repairs page, so the two routes share a cache.
  const repairsCalendar = useQuery({
    queryKey: queryKeys.repairs.list({ from, to, dateField: 'either' }),
    queryFn: () =>
      api.get<{ items: RepairCalendarItem[] }>('/repairs', {
        query: { from, to, dateField: 'either', pageSize: 500 },
      }),
    enabled: view === 'calendar' && showRepairs && canSeeRepairs,
  })

  const calendarEntries = useMemo(() => {
    const out: MonthCalendarEntry[] = []
    const todayLocal = toISODate(new Date())
    if (showRentals) {
      for (const rental of rentalsCalendar.data?.items ?? []) {
        if (rental.status === 'cancelled') continue
        const who = rental.customerName ? ` · ${rental.customerName}` : ''
        const link = (kind: 'Out' | 'Due', tone: string) => (
          <Link
            to="/rentals/$rentalId"
            params={{ rentalId: rental.id }}
            className={`${PILL} ${tone}`}
            title={`${kind === 'Out' ? 'Handed out' : 'Due back'} — ${rental.rentalNumber}${who}`}
          >
            {kind} {rental.rentalNumber}
            {who}
          </Link>
        )
        out.push({
          key: `${rental.id}-start`,
          date: rental.startDate,
          render: () => link('Out', 'bg-success-subtle text-success'),
        })
        if (rental.dueDate) {
          const late =
            rental.status === 'overdue' ||
            (rental.status !== 'returned' &&
              rental.status !== 'not_returned' &&
              rental.dueDate < todayLocal)
          out.push({
            key: `${rental.id}-due`,
            date: rental.dueDate,
            render: () =>
              link(
                'Due',
                late ? 'bg-danger-subtle font-medium text-danger' : 'bg-info-subtle text-info',
              ),
          })
        }
      }
    }
    if (showRepairs) {
      for (const ticket of repairsCalendar.data?.items ?? []) {
        const who = ticket.customerName ? ` · ${ticket.customerName}` : ''
        const link = (label: string, tone: string) => (
          <Link
            to="/repairs/$ticketId"
            params={{ ticketId: ticket.id }}
            className={`${PILL} ${tone}`}
            title={`${label} — ${ticket.ticketNumber}${who}`}
          >
            <Wrench className="me-1 inline size-2.5" aria-label="Repair" />
            {ticket.ticketNumber}
            {who}
          </Link>
        )
        const dates = [
          ['Drop-off', ticket.dropOffDate, 'bg-success-subtle text-success'],
          ['Due back to customer', ticket.promisedDate, 'bg-info-subtle text-info'],
          ['Technician booked', ticket.workDate, 'bg-warning-subtle text-warning'],
        ] as const
        for (const [label, date, tone] of dates) {
          if (date) {
            out.push({
              key: `repair-${ticket.id}-${label}`,
              date,
              render: () => link(label, tone),
            })
          }
        }
      }
    }
    return out
  }, [showRentals, showRepairs, rentalsCalendar.data, repairsCalendar.data])

  const calendarLoading =
    (showRentals && rentalsCalendar.isLoading) || (showRepairs && repairsCalendar.isLoading)
  const calendarTruncated =
    showRentals &&
    rentalsCalendar.data !== undefined &&
    rentalsCalendar.data.total > rentalsCalendar.data.items.length

  const columns = useMemo<ColumnDef<RentalRow, unknown>[]>(
    () => [
      {
        header: 'Rental',
        accessorKey: 'rentalNumber',
        cell: ({ row }) => (
          <span className="font-identifier font-medium">{row.original.rentalNumber}</span>
        ),
      },
      {
        header: 'Customer',
        accessorKey: 'customerName',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate">{row.original.customerName ?? '—'}</p>
            {row.original.customerPhone ? (
              <p className="font-identifier text-xs text-foreground-muted">
                {formatUsPhone(row.original.customerPhone)}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Unit',
        accessorKey: 'productName',
        cell: ({ row }) => {
          const kind = unitIdentifierKind(row.original.productType as never)
          return (
            <div className="min-w-0">
              <p className="truncate">
                {row.original.productName}
                {row.original.itemCount > 1 ? (
                  <span className="ms-1 text-xs text-foreground-muted">
                    +{row.original.itemCount - 1} more
                  </span>
                ) : null}
              </p>
              <p className="font-identifier text-xs text-foreground-muted">
                {UNIT_IDENTIFIER_LABELS[kind]} {row.original.imei}
              </p>
            </div>
          )
        },
      },
      {
        header: 'Fee',
        accessorKey: 'feeCents',
        cell: ({ row }) => (
          <div className="text-end">
            <p className="tabular font-medium">{formatMoney(row.original.feeCents)}</p>
            <p className="text-xs text-foreground-muted">
              {BILLING_LABEL[row.original.billingInterval] ?? row.original.billingInterval}
            </p>
          </div>
        ),
      },
      {
        header: 'Deposit',
        accessorKey: 'depositCents',
        cell: ({ row }) => (
          <span className="tabular">
            {row.original.depositCents > 0 ? formatMoney(row.original.depositCents) : '—'}
          </span>
        ),
      },
      {
        header: 'Due',
        accessorKey: 'dueDate',
        cell: ({ row }) => {
          const pastDue = isPastDue(row.original.dueDate, row.original.status)
          return (
            <span
              className={`whitespace-nowrap text-xs ${pastDue ? 'font-medium text-danger' : 'text-foreground-muted'}`}
            >
              {row.original.dueDate
                ? new Date(row.original.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : 'Open-ended'}
            </span>
          )
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <Badge tone={RENTAL_STATUS_TONE[row.original.status] ?? 'neutral'}>
              {humanizeStatus(row.original.status)}
            </Badge>
            {row.original.needsAttentionAt ? (
              <Badge tone="danger">
                <AlertTriangle /> Attention
              </Badge>
            ) : null}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        title="Rentals"
        description="Phones and SIMs rented out to customers under IMEI/ICCID tracking, not sold."
        actions={
          can('rentals:write') ? (
            <>
              <Button variant="secondary" onClick={() => setScanning(true)}>
                <ScanLine /> Scan a return
              </Button>
              <Button variant="primary" onClick={() => setCreating(true)}>
                <Plus /> New rental
              </Button>
            </>
          ) : null
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {view === 'list' ? (
          <div className="relative max-w-xs flex-1">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rental number, IMEI/ICCID, or phone…"
              className="ps-8"
            />
          </div>
        ) : (
          <MonthNav monthStart={monthStart} onChange={setMonthStart} />
        )}
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="except_cancelled">All except cancelled</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="not_returned">Not returned</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={needsAttention ? 'primary' : 'secondary'}
          aria-pressed={needsAttention}
          onClick={() => setNeedsAttention((on) => !on)}
        >
          <AlertTriangle /> Needs attention
        </Button>

        <div className="ms-auto flex flex-wrap items-center gap-2">
          {view === 'calendar' && canSeeRepairs ? (
            <SegmentedControl
              label="Show on calendar"
              value={scope}
              onChange={setScope}
              options={[
                { key: 'rentals', label: 'Rentals' },
                { key: 'repairs', label: 'Repairs' },
                { key: 'both', label: 'Both' },
              ]}
            />
          ) : null}
          <SegmentedControl
            label="Rentals view"
            value={view}
            onChange={setView}
            options={[
              { key: 'list', label: 'List' },
              { key: 'calendar', label: 'Calendar' },
            ]}
          />
        </div>
      </div>

      {view === 'calendar' ? (
        <>
          <div className="mb-4 flex flex-wrap gap-4 text-xs text-foreground-muted">
            {showRentals ? (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-success" aria-hidden="true" /> Handed out
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-info" aria-hidden="true" /> Due back
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-full bg-danger" aria-hidden="true" /> Past due
                </span>
              </>
            ) : null}
            {showRepairs ? (
              <span className="flex items-center gap-1.5">
                <Wrench className="size-3" aria-hidden="true" /> Repair ticket (drop-off, due back
                or technician booked)
              </span>
            ) : null}
          </div>
          {calendarTruncated ? (
            <Alert tone="warning" className="mb-4">
              This month has more rentals than the calendar can show. Narrow it with the status
              filter, or switch back to the list.
            </Alert>
          ) : null}
          {calendarLoading ? (
            <Skeleton className="h-[36rem] w-full" />
          ) : (
            <MonthCalendar monthStart={monthStart} entries={calendarEntries} />
          )}
        </>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          onRowClick={(row) =>
            void navigate({ to: '/rentals/$rentalId', params: { rentalId: row.id } })
          }
          infinite={{
            total,
            hasMore: Boolean(hasNextPage),
            isFetchingMore: isFetchingNextPage,
            onLoadMore: () => void fetchNextPage(),
          }}
          empty={
            search || status !== DEFAULT_STATUS ? (
              <EmptyState
                icon={Search}
                variant="no-results"
                title="No rentals match"
                description="Try a different search or status."
                onClearFilters={() => {
                  setSearch('')
                  setStatus(DEFAULT_STATUS)
                }}
              />
            ) : (
              <EmptyState
                icon={CalendarClock}
                title="No rentals yet"
                description="Phones and SIMs handed out to customers will appear here."
              />
            )
          }
        />
      )}

      <ScanReturnDialog open={scanning} onClose={() => setScanning(false)} />

      <NewRentalDialog
        open={creating}
        initialUnit={prefillUnit}
        onClose={() => {
          setCreating(false)
          setPrefillUnit(null)
        }}
      />
    </Page>
  )
}

/** The bordered button group the Repairs page uses for its view switch. */
function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: T
  onChange: (next: T) => void
  options: { key: T; label: string }[]
}) {
  return (
    <fieldset className="m-0 flex min-w-0 items-center gap-1 rounded-md border border-border p-0.5">
      <legend className="sr-only">{label}</legend>
      {options.map((opt) => (
        <Button
          key={opt.key}
          variant={value === opt.key ? 'primary' : 'ghost'}
          size="sm"
          aria-pressed={value === opt.key}
          onClick={() => onChange(opt.key)}
        >
          {opt.label}
        </Button>
      ))}
    </fieldset>
  )
}

interface CustomerRow {
  id: string
  name: string
  businessName: string | null
  phone: string | null
}

interface UnitRow {
  id: string
  productId: string
  productName: string
  productType: string
  imei: string
  locationName: string | null
}

/**
 * Step 2 of a return (t1105): staff scan the item, or key the rental number,
 * to record the return as received. A USB scanner types the code and presses
 * Enter; a match opens the rental so the verification scans can follow.
 */
function ScanReturnDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')
  const [miss, setMiss] = useState<string | null>(null)

  const scan = useMutation({
    mutationFn: (scanned: string) =>
      api.post<RentalReceiveScanResult>('/rentals/return-scan', { code: scanned }),
    onSuccess: async (result) => {
      setCode('')
      if (result.rentalId && (result.result === 'matched' || result.result === 'already_scanned')) {
        setMiss(null)
        await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
        toast.success(result.message)
        onClose()
        void navigate({ to: '/rentals/$rentalId', params: { rentalId: result.rentalId } })
      } else {
        setMiss(result.message)
        inputRef.current?.focus()
      }
    },
    onError: (err) => {
      setCode('')
      setMiss(err instanceof ApiError ? err.message : 'That scan did not go through.')
      inputRef.current?.focus()
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setCode('')
          setMiss(null)
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Scan a return</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            Scan the barcode on the item being handed in, or type the rental number.
          </p>
          <Input
            ref={inputRef}
            identifier
            autoFocus
            autoComplete="off"
            value={code}
            disabled={scan.isPending}
            placeholder="IMEI/ICCID or rental number"
            aria-label="Scanned code"
            onChange={(e) => setCode(e.target.value)}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              const scanned = code.trim()
              if (scanned) scan.mutate(scanned)
            }}
          />
          {miss ? <Alert tone="warning">{miss}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function NewRentalDialog({
  open,
  initialUnit,
  onClose,
}: {
  open: boolean
  initialUnit?: UnitRow | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [customer, setCustomer] = useState<CustomerRow | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [unit, setUnit] = useState<UnitRow | null>(null)
  const [unitSearch, setUnitSearch] = useState('')

  useEffect(() => {
    if (open && initialUnit) setUnit(initialUnit)
  }, [open, initialUnit])
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState('')
  const [feeCents, setFeeCents] = useState('')
  const [billingInterval, setBillingInterval] = useState('one_time')
  const [depositCents, setDepositCents] = useState('')

  const reset = () => {
    setCustomer(null)
    setCustomerSearch('')
    setUnit(null)
    setUnitSearch('')
    setStartDate(new Date().toISOString().slice(0, 10))
    setDueDate('')
    setFeeCents('')
    setBillingInterval('one_time')
    setDepositCents('')
  }

  const { data: customers } = useQuery({
    queryKey: queryKeys.customers.list({ q: customerSearch }),
    queryFn: () =>
      api.get<{ items: CustomerRow[] }>('/customers', {
        query: { q: customerSearch, pageSize: 6 },
      }),
    enabled: customerSearch.trim().length >= 2,
  })

  const { data: units } = useQuery({
    queryKey: queryKeys.units.list({ q: unitSearch, status: 'sellable', unassigned: true }),
    queryFn: () =>
      api.get<{ items: UnitRow[] }>('/units', {
        query: { q: unitSearch, status: 'sellable', unassigned: true, pageSize: 10 },
      }),
    enabled: unitSearch.trim().length >= 2,
  })

  const create = useMutation({
    mutationFn: () =>
      api.post('/rentals', {
        customerId: customer?.id,
        serializedUnitId: unit?.id,
        startDate,
        dueDate: dueDate || null,
        feeCents: Math.round(Number(feeCents) * 100),
        billingInterval,
        depositCents: depositCents.trim() ? Math.round(Number(depositCents) * 100) : 0,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Rental created')
      reset()
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not create the rental',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const valid =
    Boolean(customer) &&
    Boolean(unit) &&
    startDate &&
    Number(feeCents) >= 0 &&
    !Number.isNaN(Number(feeCents))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New rental</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Customer" required>
            {() =>
              customer ? (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{customer.name}</p>
                    <p className="font-identifier text-xs text-foreground-muted">
                      {customer.phone ? formatUsPhone(customer.phone) : '—'}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCustomer(null)}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Name or phone…"
                  />
                  {customers?.items.length ? (
                    <ul className="divide-y divide-border rounded-lg border border-border">
                      {customers.items.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                            onClick={() => {
                              setCustomer(row)
                              setCustomerSearch('')
                            }}
                          >
                            <span className="block font-medium">{row.name}</span>
                            <span className="block font-identifier text-2xs text-foreground-muted">
                              {row.phone ? formatUsPhone(row.phone) : '—'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )
            }
          </Field>

          <Field
            label="Device"
            required
            hint="Search by IMEI, ICCID, or product name — only sellable units show up."
          >
            {() =>
              unit ? (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{unit.productName}</p>
                    <p className="font-identifier text-xs text-foreground-muted">
                      {UNIT_IDENTIFIER_LABELS[unitIdentifierKind(unit.productType as never)]}{' '}
                      {unit.imei}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setUnit(null)}>
                    Change
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    value={unitSearch}
                    onChange={(e) => setUnitSearch(e.target.value)}
                    placeholder="IMEI, ICCID, or product…"
                  />
                  {units?.items.length ? (
                    <ul className="divide-y divide-border rounded-lg border border-border">
                      {units.items.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                            onClick={() => {
                              setUnit(row)
                              setUnitSearch('')
                            }}
                          >
                            <span className="block font-medium">{row.productName}</span>
                            <span className="block font-identifier text-2xs text-foreground-muted">
                              {UNIT_IDENTIFIER_LABELS[unitIdentifierKind(row.productType as never)]}{' '}
                              {row.imei}
                              {row.locationName ? ` — ${row.locationName}` : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )
            }
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" required>
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Due date" hint="Leave blank for an open-ended rental.">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label={
                rentalIntervalUnit(billingInterval)
                  ? `Rate per ${rentalIntervalUnit(billingInterval)}`
                  : 'Fee'
              }
              required
              hint={(() => {
                const unit = rentalIntervalUnit(billingInterval)
                if (unit !== 'day' && unit !== 'week') return undefined
                if (!dueDate) return `Add a due date to charge by the ${unit}.`
                const periods = rentalFeePeriods({ billingInterval, startDate, dueDate })
                const rate = Math.round(Number(feeCents) * 100) || 0
                return `${formatMoney(rate)} × ${periods} ${unit}${periods === 1 ? '' : 's'} = ${formatMoney(rate * periods)}`
              })()}
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={feeCents}
                  onChange={(e) => setFeeCents(e.target.value)}
                />
              )}
            </Field>
            <Field label="Billing">
              {(props) => (
                <Select value={billingInterval} onValueChange={setBillingInterval}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One time</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>

          <Field
            label="Deposit"
            hint="Recorded, not charged automatically — take it at the counter."
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={0}
                step="0.01"
                value={depositCents}
                onChange={(e) => setDepositCents(e.target.value)}
              />
            )}
          </Field>

          <Alert tone="info">
            No automatic recurring billing yet — staff record each fee payment from the rental page.
          </Alert>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create rental
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
